import type { LatLonPosition } from '@types';

/**
 * @typedef {'dev' | 'local' | 'prod' | 'qa' | 'stage' | 'test'} AnalyticsEnvironment
 * Represents the environment in which the analytics event is occurring.
 */
export type AnalyticsEnvironment =
  | 'dev'
  | 'local'
  | 'prod'
  | 'preprod'
  | 'mobile'
  | 'qa'
  | 'stage'
  | 'test';

/**
 * @typedef {'CampusNavigation' | 'CampusDirectory'} AnalyticsOrigin
 * Represents the origin of the analytics event.
 */
export type AnalyticsOrigin =
  | 'CampusNavigation'
  | 'CampusDirectory'
  | 'Header'
  | 'TutorialModal';

/**
 * @typedef {'web' | 'kiosk'} AnalyticsProduct
 * Represents the product from which the analytics event is generated.
 */
export type AnalyticsProduct = 'web' | 'kiosk';

// Define the analytics event names as a single array
export const ANALYTICS_EVENT_NAMES = [
  'CTAModal',
  'DestinationSearch',
  'DestinationSearchSelected',
  'GlobalCampusSearch',
  'GlobalCampusSearchSelected',
  'QRCodeUsed',
  'SetStartingLocation',
  'SetDestination',
  'ShareToEmail',
  'ShareToPhone',
  'ShareToPrint',
  'StartingLocationSearch',
  'StartingLocationSearchSelected',
  'ViewDirections',
  'PageLoad',
] as const;

// Create the type from the array
export type AnalyticsEventName = (typeof ANALYTICS_EVENT_NAMES)[number];

/**
 * @typedef {Object} AnalyticsCommonProperties
 * @property {AnalyticsEnvironment} env - The environment in which the event is occurring.
 * @property {AnalyticsProduct} product - The product from which the event is generated.
 * @property {string} appId - The application ID.
 */
export type AnalyticsCommonProperties = {
  env: AnalyticsEnvironment;
  product: AnalyticsProduct;
  appId: string;
  client: string;
  appVersion: string;
};

/**
 * @typedef {Object} AnalyticsCTAModal
 * @property {AnalyticsOrigin} origin - The origin of the CTA modal event.
 */
export type AnalyticsCTAModal = {
  origin: AnalyticsOrigin;
};

/**
 * @typedef {Object} AnalyticsDestinationSearch
 * @property {string} value - The search value.
 * @property {number} results - The number of search results.
 */
export type AnalyticsDestinationSearch = {
  value: string;
  results: number;
};

/**
 * @typedef {Object} AnalyticsDestinationSearchSelected
 * @property {number} destinationId - The ID of the selected destination.
 * @property {string} value - The search value.
 */
export type AnalyticsDestinationSearchSelected = {
  destinationId: number;
  value: string;
};

/**
 * @typedef {Object} AnalyticsGlobalCampusSearch
 * @property {string} value - The search value.
 * @property {number} results - The number of search results.
 */
export type AnalyticsGlobalCampusSearch = {
  value: string;
  results: number;
};

/**
 * @typedef {Object} AnalyticsGlobalCampusSearchSelected
 * @property {number} destinationId - The ID of the selected destination.
 * @property {number} resultSlot - The slot of the result.
 */
export type AnalyticsGlobalCampusSearchSelected = {
  destinationId: number;
  resultSlot: number;
};

/**
 * Represents the data structure for an analytics event when a QR code is used.
 *
 * @property {number} destinationId - The ID of the destination associated with the QR code.
 * @property {string} [kioskId] - Optional ID of the kiosk where the QR code was used.
 * @property {string} [startingAddress] - Optional starting address associated with the QR code usage.
 * @property {number} [startingId] - Optional starting ID associated with the QR code usage.
 */
export type AnalyticsQRCodeUsed = {
  destinationId: number;
  kioskId?: string;
  startingAddress?: string;
  startingId?: number;
};

/**
 * @typedef {Object} AnalyticsSetDestination
 * @property {number} destinationId - The destination ID.
 */
export type AnalyticsSetDestination = {
  destinationId: number;
};

/**
 * @typedef {Object} AnalyticsSetStartingLocationCommon
 * This type is defined as a `LatLonPosition`.
 */
export type AnalyticsSetStartingLocationCommon = LatLonPosition;

/**
 * @typedef {Object} AnalyticsSetStartingLocationDestinationId
 * @property {number} destinationId - The ID of the starting location.
 */
export type AnalyticsSetStartingLocationDestinationId = {
  destinationId: number;
};

/**
 * @typedef {Object} AnalyticsSetStartingLocationAddress
 * @property {string} address - The address of the starting location.
 */
export type AnalyticsSetStartingLocationAddress = {
  address: string;
};

/**
 * @typedef {Partial<AnalyticsSetStartingLocationAddress> & Partial<AnalyticsSetStartingLocationDestinationId> & AnalyticsSetStartingLocationCommon} AnalyticsSetStartingLocation
 * Represents the starting location for analytics.
 */
export type AnalyticsSetStartingLocation =
  Partial<AnalyticsSetStartingLocationAddress> &
    Partial<AnalyticsSetStartingLocationDestinationId> &
    AnalyticsSetStartingLocationCommon;

/**
 * @typedef {Object} AnalyticsShare
 * @property {string} [email] - The email address for sharing.
 */
export type AnalyticsShare = {
  email?: string;
};

/**
 * @typedef {Object} AnalyticsStartingLocationSearch
 * @property {string} value - The search value.
 * @property {number} addressResults - The number of address results.
 * @property {number} locationResults - The number of location results.
 */
export type AnalyticsStartingLocationSearch = {
  value: string;
  addressResults: number;
  locationResults: number;
};

/**
 * @typedef {Object} AnalyticsStartingLocationSearchSelected
 * @property {string} value - The search value.
 * @property {string} address - The selected address.
 */
export type AnalyticsStartingLocationSearchSelected = {
  value: string;
  address: string;
};

/**
 * @typedef {Object} AnalyticsViewDirectionsCommon
 * @property {number | undefined} destinationId - The destination ID.
 * @property {number | null} driveTime - The drive time.
 */
export type AnalyticsViewDirectionsCommon = {
  destinationId: number | undefined;
  driveTime: number | null;
};

/**
 * @typedef {Object} AnalyticsViewDirectionsKiosk
 * @property {string} kioskId - The ID of the kiosk.
 */
export type AnalyticsViewDirectionsKiosk = {
  kioskId: string;
};

/**
 * @typedef {Object} AnalyticsViewDirectionsWeb
 * @property {string} address - The address for web directions.
 */
export type AnalyticsViewDirectionsWeb = {
  address: string;
};

/**
 * @typedef {Partial<AnalyticsViewDirectionsWeb> & Partial<AnalyticsViewDirectionsKiosk> & AnalyticsViewDirectionsCommon} AnalyticsViewDirections
 * Represents the event for when a user views directions.
 */
export type AnalyticsViewDirections = Partial<AnalyticsViewDirectionsWeb> &
  Partial<AnalyticsViewDirectionsKiosk> &
  AnalyticsViewDirectionsCommon;

/**
 * @typedef {AnalyticsCTAModal | AnalyticsDestinationSearch | AnalyticsDestinationSearchSelected | AnalyticsGlobalCampusSearch | AnalyticsGlobalCampusSearchSelected | AnalyticsQRCodeUsed | AnalyticsSetDestination | AnalyticsSetStartingLocation | AnalyticsStartingLocationSearch | AnalyticsStartingLocationSearchSelected | AnalyticsShare | AnalyticsViewDirections} AnalyticsEventProperties
 * Represents the properties of an analytics event.
 */
export type AnalyticsEventProperties =
  | AnalyticsCTAModal
  | AnalyticsDestinationSearch
  | AnalyticsDestinationSearchSelected
  | AnalyticsGlobalCampusSearch
  | AnalyticsGlobalCampusSearchSelected
  | AnalyticsQRCodeUsed
  | AnalyticsSetDestination
  | AnalyticsSetStartingLocation
  | AnalyticsStartingLocationSearch
  | AnalyticsStartingLocationSearchSelected
  | AnalyticsShare
  | AnalyticsViewDirections;
